import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, ArrowLeft, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logError } from '@/utils/errorLogger';

/**
 * Page-level Error Boundary
 * Catches render errors and displays a full-page fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false,
      errorLogged: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    
    // Log error to backend
    const { jobId, jobNumber, componentName, page } = this.props;
    logError({
      error,
      errorInfo,
      jobId,
      jobNumber,
      componentName: componentName || 'PageRoot',
      page: page || window.location.pathname,
      boundaryType: 'page'
    }).then(() => {
      this.setState({ errorLogged: true });
    }).catch(console.error);
    
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoBack = () => {
    window.history.back();
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { jobNumber, fallbackMessage } = this.props;
      
      return (
        <div className="min-h-screen bg-aa-bg flex items-center justify-center p-6">
          <div className="bg-white rounded-aa-lg shadow-aa-card p-8 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Something went wrong
                </h2>
                <p className="text-sm text-gray-500">
                  {fallbackMessage || 'An error occurred while loading this page.'}
                </p>
              </div>
            </div>

            {/* Job ID display */}
            {jobNumber && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Job ID:</span> {jobNumber}
                </p>
              </div>
            )}

            <p className="text-gray-600 mb-6">
              This might be due to invalid data or a temporary issue. 
              You can try reloading the page or return to the dashboard.
            </p>

            <div className="flex gap-3 mb-4">
              <Button 
                onClick={this.handleReload}
                className="flex-1 bg-aa-primary hover:bg-aa-primary/90"
                data-testid="error-reload-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button 
                variant="outline" 
                onClick={this.handleGoHome}
                className="flex-1"
                data-testid="error-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>

            <Button 
              variant="ghost" 
              onClick={this.handleGoBack}
              className="w-full mb-4"
              data-testid="error-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs Board
            </Button>

            {/* Error logged indicator */}
            {this.state.errorLogged && (
              <p className="text-xs text-gray-400 text-center mb-2">
                <Bug className="w-3 h-3 inline mr-1" />
                Error has been logged for review
              </p>
            )}

            {/* Error Details Toggle */}
            <button
              onClick={this.toggleDetails}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              data-testid="error-details-toggle"
            >
              {this.state.showDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {this.state.showDetails ? 'Hide' : 'View'} error details
            </button>

            {this.state.showDetails && this.state.error && (
              <div className="mt-4 p-4 bg-gray-100 rounded-lg overflow-auto max-h-48">
                <p className="text-sm font-mono text-red-600 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Section-level Error Boundary
 * Catches errors in a section and displays an inline error card
 * Other sections continue to function normally
 */
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    
    // Log error to backend
    const { sectionName, jobId, jobNumber } = this.props;
    logError({
      error,
      errorInfo,
      jobId,
      jobNumber,
      componentName: sectionName || 'UnknownSection',
      page: window.location.pathname,
      boundaryType: 'section'
    }).catch(console.error);
    
    console.error(`SectionErrorBoundary [${sectionName}] caught error:`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prev => ({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: prev.retryCount + 1 
    }));
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { sectionName, fallbackHeight = 'auto' } = this.props;
      
      return (
        <div 
          className="bg-red-50 border border-red-200 rounded-aa-lg p-4"
          style={{ minHeight: fallbackHeight }}
          data-testid={`section-error-${sectionName?.toLowerCase().replace(/\s/g, '-')}`}
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-red-800">
                {sectionName || 'Section'} failed to load
              </h4>
              <p className="text-xs text-red-600 mt-1">
                This section encountered an error but the rest of the page should work normally.
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={this.handleRetry}
                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid="section-retry-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry
                </Button>
                <button
                  onClick={this.toggleDetails}
                  className="text-xs text-red-500 hover:text-red-700 underline"
                >
                  {this.state.showDetails ? 'Hide details' : 'Show details'}
                </button>
              </div>

              {this.state.showDetails && this.state.error && (
                <div className="mt-3 p-2 bg-red-100/50 rounded text-xs font-mono text-red-700 overflow-auto max-h-32">
                  {this.state.error.toString()}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
export { SectionErrorBoundary };
